// Document state: the annots+baseline reducer with undo/redo history,
// and the multi-document tab model. See the route's header comment for
// how baseline rebasing makes page ops undoable.
import type { Annotation, FormValues, PageInfo, WidgetInfo } from "./protocol.ts";

let annotIdSeq = 0;
export function nextAnnotId(): string {
  annotIdSeq += 1;
  return `a${Date.now().toString(36)}${annotIdSeq}`;
}

// ─────────────────── annotations + undo/redo reducer ───────────────────
//
// The state pairs the annotation overlay with the document *baseline*:
// the bytes the worker has open and re-applies annotations to on save.
// Annotation edits share the current baseline; page operations rebase
// to fresh bytes. History snapshots hold {annots, baseline} references,
// which is what makes page ops undoable — undoing across a baseline
// change re-opens the older bytes in the worker (see the sync effect).

export interface Baseline {
  bytes: ArrayBuffer;
  pages: PageInfo[];
  /** AcroForm widget definitions found in these bytes. */
  widgets: WidgetInfo[];
  /** Monotonic id — the engine-sync effect uses it to notice that the
   *  worker has a different document open than the state says. */
  gen: number;
}

let genSeq = 0;
export function nextGen(): number {
  genSeq += 1;
  return genSeq;
}

export interface DocSnap {
  annots: Annotation[];
  baseline: Baseline | null;
}

export interface DocState {
  annots: Annotation[];
  baseline: Baseline | null;
  past: DocSnap[];
  future: DocSnap[];
  /** Set when the newest history entry came from a coalescable commit
   *  (e.g. an arrow-key nudge) — a same-key commit arriving within
   *  COALESCE_MS merges into it instead of pushing a new entry, so a
   *  held arrow key is ONE undo step. */
  lastCoalesce?: { key: string; at: number };
}

/** Same-key commits within this window merge into one history entry. */
export const COALESCE_MS = 800;

export type DocAction =
  | {
      type: "commit";
      annots: Annotation[];
      base?: Annotation[];
      baseline?: Baseline;
      /** Merge with the previous commit when its key matches and it
       *  happened less than COALESCE_MS ago. `at` is passed in (not
       *  read in the reducer) to keep the reducer pure. */
      coalesce?: { key: string; at: number };
    }
  | { type: "transient"; annots: Annotation[] }
  | { type: "undo" }
  | { type: "redo" }
  | { type: "reset" }
  // Tab switch: swap in the target tab's stashed state wholesale.
  | { type: "hydrate"; state: DocState };

export const EMPTY_DOC: DocState = { annots: [], baseline: null, past: [], future: [] };

export function docReducer(state: DocState, action: DocAction): DocState {
  switch (action.type) {
    case "commit": {
      const merge =
        action.coalesce &&
        state.lastCoalesce &&
        action.coalesce.key === state.lastCoalesce.key &&
        action.coalesce.at - state.lastCoalesce.at < COALESCE_MS;
      if (merge) {
        // The pre-burst snapshot is already on `past` — just move the
        // live annots and refresh the coalesce clock.
        return {
          ...state,
          annots: action.annots,
          baseline: action.baseline ?? state.baseline,
          future: [],
          lastCoalesce: action.coalesce,
        };
      }
      return {
        annots: action.annots,
        baseline: action.baseline ?? state.baseline,
        past: [
          ...state.past,
          { annots: action.base ?? state.annots, baseline: state.baseline },
        ].slice(-100),
        future: [],
        lastCoalesce: action.coalesce,
      };
    }
    case "transient":
      return { ...state, annots: action.annots };
    case "undo": {
      const prev = state.past[state.past.length - 1];
      if (!prev) return state;
      return {
        annots: prev.annots,
        baseline: prev.baseline,
        past: state.past.slice(0, -1),
        future: [{ annots: state.annots, baseline: state.baseline }, ...state.future],
      };
    }
    case "redo": {
      const next = state.future[0];
      if (!next) return state;
      return {
        annots: next.annots,
        baseline: next.baseline,
        past: [...state.past, { annots: state.annots, baseline: state.baseline }].slice(-100),
        future: state.future.slice(1),
      };
    }
    case "reset":
      return EMPTY_DOC;
    case "hydrate":
      // Never inherit a coalesce window across tab switches.
      return { ...action.state, lastCoalesce: undefined };
  }
}

// ─────────────────────────── tabs ───────────────────────────

export interface DocTab {
  id: string;
  name: string;
  /** Pristine original bytes — saves re-apply annotations to these. */
  bytes: ArrayBuffer;
  /** Annotations + undo history, parked here while the tab is inactive
   *  (the active tab's live state lives in the reducer). */
  stash: DocState;
  /** User-entered AcroForm values (not part of undo history). */
  formValues: FormValues;
  /** Password that unlocked an encrypted doc. Kept in memory only —
   *  never written to the IndexedDB session — so a reload re-prompts. */
  password?: string;
}

let tabSeq = 0;
export function nextTabId(): string {
  tabSeq += 1;
  return `pdf-${Date.now().toString(36)}-${tabSeq}`;
}

export function untitledName(tabs: DocTab[]): string {
  const n = tabs.filter((t) => /^Untitled( \d+)?\.pdf$/.test(t.name)).length;
  return n === 0 ? "Untitled.pdf" : `Untitled ${n + 1}.pdf`;
}

export function isPdfFile(f: File): boolean {
  return f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf");
}

/** Files the markdown importer accepts (.txt is valid markdown too). */
export function isMdFile(f: File): boolean {
  return (
    f.type === "text/markdown" || /\.(md|markdown|mdown|txt)$/i.test(f.name)
  );
}
